import DropDownBox from "devextreme-react/drop-down-box";
import TreeView from "devextreme-react/tree-view";
import React, { useMemo, useState } from "react";
import { TreeSelectNode } from "./TreeSelect";

type Props = {
    nodes: TreeSelectNode[];
    // Every selected node key (group + leaf). Caller filters by membership against these.
    selectedKeys: string[];
    onChange: (keys: string[]) => void;
    placeholder?: string;
    width?: number | string;
    dropdownWidth?: number;
    dropdownHeight?: number;
    treeHeight?: number;
};

// A DropDownBox whose panel is a checkbox TreeView, for selecting multiple hierarchy nodes.
// Reuses the TreeSelectNode shape from TreeSelect. Checking a parent cascades to its descendants,
// so the caller receives every selected key (module/task/subtask) and matches notes by membership.
export default function TreeMultiSelect({
    nodes,
    selectedKeys,
    onChange,
    placeholder = "All",
    width = 320,
    dropdownWidth = 480,
    dropdownHeight = 340,
    treeHeight = 290
}: Props) {
    const [opened, setOpened] = useState(false);

    // Module group rows (id "m:*") shouldn't inflate the count shown in the input.
    const selectedCount = useMemo(
        () => selectedKeys.filter(k => !k.startsWith("m:")).length,
        [selectedKeys]
    );

    // A single synthetic item is enough for the input to render the summary label.
    const inputDataSource = useMemo(
        () => (selectedCount ? [{ key: "sel", label: `${selectedCount} selected` }] : []),
        [selectedCount]
    );

    const contentRender = () => (
        <TreeView
            dataSource={nodes as unknown as Record<string, unknown>[]}
            keyExpr="id"
            parentIdExpr="parentId"
            displayExpr="text"
            dataStructure="plain"
            showCheckBoxesMode="normal"
            selectNodesRecursive={true}
            selectByClick={true}
            searchEnabled
            searchMode="contains"
            searchExpr="text"
            height={treeHeight}
            selectedItemKeys={selectedKeys}
            onSelectionChanged={(e: any) => {
                const keys: string[] = e.component.getSelectedNodeKeys();
                // Skip redundant updates that could ping-pong with the controlled prop.
                if (keys.length !== selectedKeys.length || keys.some(k => !selectedKeys.includes(k))) {
                    onChange(keys);
                }
            }}
        />
    );

    return (
        <DropDownBox
            value={selectedCount ? "sel" : null}
            opened={opened}
            onOptionChanged={(e) => {
                if (e.name === "opened") setOpened(!!e.value);
            }}
            dataSource={inputDataSource}
            valueExpr="key"
            displayExpr="label"
            placeholder={placeholder}
            showClearButton={true}
            onValueChanged={(e) => {
                // Clear button sets the value to null.
                if (e.value === null && selectedKeys.length) onChange([]);
            }}
            width={width}
            dropDownOptions={{ width: dropdownWidth, height: dropdownHeight }}
            contentRender={contentRender}
        />
    );
}
